import { useState, useContext, useMemo, useCallback } from 'react';

import {
  Box,
  IconButton,
  Text,
  HStack,
  Divider,
  CloseButton,
  Badge,
  Flex,
  Button,
  Spinner,
} from '@chakra-ui/react';
import { useRouter } from 'next/router';

import style from './NotificationBanner.style';
import VendorRatingModal from '../VendorRatingModal/vendorRatingModal';

import { Icon, Modal } from '@/components';
import CenteredLoader from '@/components/organism/dashboard/CenteredLoader/CenteredLoader';
import { IVendor } from '@/components/organism/dashboard/Profile/Profile.type';
import useGetVendors from '@/components/organism/dashboard/Profile/hooks/useGetVendors';
import { transformVendorData } from '@/components/organism/dashboard/Profile/utils';
import AppRoutes from '@/constants/AppRoutes';
import { convertTimeZoneNotification } from '@/helpers/date';
import { mapStageNameToNavigationRoute } from '@/helpers/mapStageNametoRoutes';
import { consultantDeList, contractorDeList } from '@/helpers/utils';
import useTranslation from '@/hooks/useTranslate';
import { DashboardContext } from '@/providers/DashboardContext';
import {
  INotificationOptionalType,
  ISiteVisit,
  IVendorType,
} from '@/types/dashboardContext.type';

const NotificationBanner = () => {
  const { t } = useTranslation();
  const router = useRouter();
  const {
    notificationDetails,
    markAsReadNotification,
    vendorRatingLoading,
    submitVendorRating,
    vendorRatingSubmitLoader,
    updateUserContext,
    getServiceStatus,
    updateDashboardState,
    dashboardVendorData,
  } = useContext(DashboardContext);
  const { vendors } = useGetVendors();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isOpenModal, setIsOpenModal] = useState<boolean>(false);
  const [isOpenValidModal, setIsOpenValidModal] = useState<boolean>(false);
  const [formattedData, setFormattedData] = useState<any>([]);
  const [isSiteVisit, setSiteVisit] = useState<boolean>(false);
  const [deListedModal, setDeListedModal] = useState<boolean>(false);
  const vendorDetails = transformVendorData(vendors);

  /* Added for Redirection */
  const handleNavigateToNotificationDetails = useCallback(
    async (sfStageName: string | undefined, serviceRequestId: string) => {
      try {
        const slug = mapStageNameToNavigationRoute(sfStageName);
        const response: any = await getServiceStatus(serviceRequestId);
        const status = response?.serviceRequestStatus;
        if (status && status !== 'In Progress') {
          return router.push(`${slug}?id=${serviceRequestId}/${status}`);
        } else {
          return router.push(`${slug}?id=${serviceRequestId}`);
        }
      } catch (error) {
        console.error(error, 'Error on Navigation function');
      }
    },
    []
  );

  const notificationCount = useMemo(() => {
    return Array.isArray(notificationDetails)
      ? notificationDetails.filter(item => !item.isRead).length
      : 0;
  }, [notificationDetails]);

  const filteredData = useCallback(
    (notificationIds: string | any) => {
      const result = vendorDetails.filter((vendorItem: IVendor) =>
        notificationIds.includes(vendorItem?.id)
      );
      return result?.length > 0 ? result : false;
    },
    [vendors]
  );

  const handleNotificationClick = ({
    isRead,
    id,
    notificationType,
    sfStageName,
    serviceRequestId,
    notificationData,
  }: INotificationOptionalType) => {
    try {
      setIsOpen(false);
      updateDashboardState({ isNotificationClick: true }); // Added to update notification state in context
      if (!isRead) {
        markAsReadNotification({ id, isRead: true });
      }

      switch (notificationType) {
        case 'vendorRatingSurvey':
          handleVendorRating(notificationData);
          break;
        case 'siteVisitReschedule':
        case 'siteVisit':
        case 'siteVisitReminder':
          handleSiteVisit(notificationData, sfStageName, serviceRequestId);
          break;
        case 'nonResponsiveCustomer':
          handleNavigateToNotificationDetails(sfStageName, serviceRequestId);
          break;
        case 'consultantDelist':
        case 'contractorDelist':
          handleDeListedVendorNavigation(notificationType);
          break;
        default:
          handleNavigateToNotificationDetails(sfStageName, serviceRequestId);
          break;
      }
    } catch (error) {
      updateDashboardState({ isNotificationClick: false });
      console.error(error, 'Error while clicking on notification details');
    }
  };

  /* To handle vendor rating */
  const handleVendorRating = (notificationData: IVendorType) => {
    try {
      const isValid = filteredData(notificationData?.vendorIds);
      if (!isValid) {
        setIsOpenValidModal(true);
      } else {
        setFormattedData(isValid);
        setIsOpenModal(true);
      }
    } catch (error) {
      console.error(error, 'Error on vendorRating function');
    }
  };

  /* To handle Site visit */
  const handleSiteVisit = (
    notificationData: ISiteVisit,
    sfStageName: string,
    serviceRequestId: string
  ) => {
    try {
      const { isExpired } = convertTimeZoneNotification(notificationData);
      if (!isExpired) {
        setSiteVisit(true);
        setIsOpenValidModal(true);
      } else {
        handleNavigateToNotificationDetails(sfStageName, serviceRequestId);
      }
    } catch (error) {
      console.error(error, 'Error on handleSiteVisit function');
    }
  };

  /* To handle DeListed vendor */
  const handleDeListedVendorNavigation = async (details: string) => {
    try {
      const consultant = dashboardVendorData?.awardedConsultant;
      const contractor = dashboardVendorData?.awardedContractor;

      const isConsultantDeListed =
        consultant?.isDelist && consultant?.isDelistedVendorBannerActive;
      const isContractorDeListed =
        contractor?.isDelist && contractor?.isDelistedVendorBannerActive;

      // Check the selected role and respond accordingly
      if (details === consultantDeList) {
        if (!isConsultantDeListed) {
          return setDeListedModal(true);
        }
        return router.push(AppRoutes.Dashboard.DeListConsultant);
      }

      if (details === contractorDeList) {
        if (!isContractorDeListed) {
          return setDeListedModal(true);
        }
        return router.push(AppRoutes.Dashboard.DeListContractor);
      }
    } catch (error) {
      console.error('Error occurred in handleDeListedVendorNavigation', error);
    }
  };

  const handleClose = () => {
    setIsOpenModal(false);
  };

  /* Added for Submit Vendor Rating Form */
  const handleSubmit = async (data: any) => {
    try {
      const response: any = await submitVendorRating(data);
      if (response) {
        setIsOpenModal(false);
        await updateUserContext();
      }
    } catch (error) {
      console.error(
        error,
        'Error while submitting vendor rating Form from notification banner'
      );
    }
  };

  const handleCloseConditionalModal = () => {
    setIsOpenValidModal(false);
    setDeListedModal(false);
  };

  return (
    <Box sx={style.baseStyle?.root}>
      <Box>
        <Box sx={style.baseStyle?.initialDiv}>
          <IconButton
            icon={<Icon name="bellIcon" />}
            aria-label="Notifications"
            onClick={() => setIsOpen(!isOpen)}
            variant="ghost"
            fontSize="1.8rem"
            position="relative"
            _hover={{
              backgroundColor: 'white',
            }}
          />

          {vendorRatingLoading ? ( // Show loader when fetching count
            <Badge sx={style.baseStyle?.badge}>
              <Spinner size="xs" color="white" />
            </Badge>
          ) : (
            notificationCount > 0 && (
              <Badge sx={style.baseStyle?.badge}>{notificationCount}</Badge>
            )
          )}
        </Box>

        {isOpen && (
          <Box sx={style.baseStyle?.body}>
            <Box sx={style.baseStyle?.secondDiv}>
              <HStack justifyContent="space-between">
                <Text sx={style.baseStyle?.header}>
                  {t('portal_notifications_screen_title')}
                </Text>
                <CloseButton
                  onClick={() => setIsOpen(!isOpen)}
                  sx={style.baseStyle?.closeIcon}
                />
              </HStack>
            </Box>
            <Divider />
            <Box
              sx={
                notificationDetails?.length > 5
                  ? style.baseStyle?.scrollDiv
                  : style.baseStyle?.thirdDiv
              }
            >
              {vendorRatingLoading ? (
                <CenteredLoader variant="guideSkeleton" />
              ) : Array.isArray(notificationDetails) &&
                notificationDetails.length > 0 ? (
                notificationDetails?.map((item: any, index) => {
                  return (
                    <Box
                      key={item?.id}
                      sx={{
                        ...style.baseStyle?.conditionFalse,
                        bg: item?.isRead ? '#F7FAFC' : 'white', // Gray out if read
                        opacity: item?.isRead ? 0.6 : 1, // Reduce opacity if read
                      }}
                    >
                      <Box
                        cursor="pointer"
                        onClick={() => {
                          handleNotificationClick(item);
                        }}
                        sx={style.baseStyle?.initialBox}
                      >
                        <Box sx={style.baseStyle?.innerBox}>
                          <HStack sx={style.baseStyle?.initialHStack}>
                            <Box sx={style.baseStyle?.icon}>
                              <Icon
                                name={item?.isRead ? 'greyDots' : 'yellowDots'}
                                sx={style.baseStyle?.iconStyle}
                              />
                            </Box>
                            <Text sx={style.baseStyle?.title}>
                              {item?.title}
                            </Text>
                          </HStack>
                          <Text sx={style.baseStyle?.description}>
                            {item?.body}
                          </Text>
                        </Box>
                      </Box>

                      {index < notificationDetails.length - 1 && (
                        <Divider mt={4} />
                      )}
                    </Box>
                  );
                })
              ) : (
                <Box sx={style.baseStyle?.noDetails}>
                  <Text sx={style.baseStyle?.noContent}>
                    {t('portal_notification_empty')}
                  </Text>
                </Box>
              )}
            </Box>
          </Box>
        )}
      </Box>
      <Box>
        <VendorRatingModal
          isOpen={isOpenModal}
          FormSubmit={handleSubmit}
          onClose={handleClose}
          data={formattedData}
          vendorRatingLoading={vendorRatingSubmitLoader}
        ></VendorRatingModal>
      </Box>
      <Box>
        <Modal
          isOpen={isOpenValidModal || deListedModal}
          onClose={handleCloseConditionalModal}
          showCloseButton={false}
          closeOnOverlayClick={false}
          size={'4xl'}
        >
          <Flex direction={'column'} gap={isSiteVisit ? '20px' : '16px'}>
            <Flex justifyContent={'center'}>
              <Icon name="arrows" sx={style.baseStyle?.modalIcon} />
            </Flex>

            {isSiteVisit ? (
              <Flex direction={'column'}>
                <Text sx={style.baseStyle?.siteVisitTitle}>
                  {t('portal_site_visit_expiry_title')}
                </Text>
              </Flex>
            ) : (
              <Flex direction={'column'}>
                <Text sx={style.baseStyle?.siteVisitTitle}>
                  {t('portal_notification_popup_completed_Label')}
                </Text>
              </Flex>
            )}

            {!deListedModal ? (
              <Flex direction={'column'}>
                <Text
                  sx={
                    isSiteVisit
                      ? style.baseStyle?.siteVisitDescription
                      : style.baseStyle?.validTitle
                  }
                >
                  {!isSiteVisit
                    ? t('portal_conditional_modal_tile')
                    : t('portal_site_visit_expiry_description')}
                </Text>
              </Flex>
            ) : (
              <Flex direction={'column'}>
                <Text sx={style.baseStyle?.siteVisitDescription}>
                  {t('portal_delisted_completed_modal_description')}
                </Text>
              </Flex>
            )}

            <Flex justifyContent={'center'} mt={'10px'}>
              <Button
                variant="solid"
                onClick={handleCloseConditionalModal}
                sx={style.baseStyle?.closeButton}
              >
                {t('close')}
              </Button>
            </Flex>
          </Flex>
        </Modal>
      </Box>
    </Box>
  );
};

export default NotificationBanner;
